import React from 'react'

import {
  AlignItems,
  Body2,
  Box,
  Button,
  ButtonColorVariant,
  ButtonVariant,
  Color,
  Display,
  FlexDirection,
  Heading6,
  Icon,
  IconVariant,
  Image,
  JustifyContent,
  Overflow,
} from '@gousto-internal/citrus-react'
import { useSelector } from 'react-redux'

import { FlexPortionSummary } from '@features/recipe-tile'
import { RecipeSubtitle } from '@features/recipe-tile/src/components/RecipeSubtitle'
import { isCaloriePreferenceSelected } from '@library/api-menu-experience'
import type { MenuPreferences } from '@library/api-menu-experience'
import type { Recipe } from '@library/menu-service'
import { queueTrackingEvent } from '@library/tracking-integration'

import type { AddRecipeByUuidFn, RemoveRecipeByUuidFn } from 'routes/Menu/domains/basket'
import { getBasketRecipeNumPortionsForId, getBasketRecipeQuantityForId } from 'selectors/basket'

import type { SearchRecipeTileTrackingData, TrackCTAClick } from '../types'
import { InvalidPreferenceTag } from './InvalidPreferenceTag'

const IMAGE_WIDTH = 200
const TILE_IMAGE_SIZE = '112px'
const TILE_FOOTER_HEIGHT = '40px'
const TILE_HEIGHT = '152px'
const ACTION_BUTTON_SIZE = '40px'

type FlexSearchRecipeTileProps = {
  addRecipe: AddRecipeByUuidFn
  menuPreferences?: MenuPreferences
  onClick: () => void
  onEditFlexPortions?: (recipe: Recipe, trackingData: SearchRecipeTileTrackingData) => void
  reachedLimit: boolean
  recipe: Recipe
  removeRecipe: RemoveRecipeByUuidFn
  showCustomisedTileFooter?: boolean
  trackingData: SearchRecipeTileTrackingData
}

export function FlexSearchRecipeTile({
  addRecipe,
  menuPreferences,
  onClick,
  onEditFlexPortions,
  reachedLimit,
  recipe,
  removeRecipe,
  showCustomisedTileFooter = false,
  trackingData,
}: FlexSearchRecipeTileProps) {
  const recipeImage = recipe.images?.[0]?.crops?.find(({ width }) => width === IMAGE_WIDTH) ?? null
  const recipeCount = useSelector(getBasketRecipeQuantityForId(recipe.id))
  const recipeNumPortions = useSelector(getBasketRecipeNumPortionsForId(recipe.id))
  const isInBasket = recipeCount > 0
  const shouldShowCalories = isCaloriePreferenceSelected(menuPreferences?.display?.list)

  const trackingProperties: TrackCTAClick['properties'] = {
    search_invocation_id: trackingData.invocationId,
    search_rank: trackingData.index,
    menu_id: trackingData.menuId,
    add_type: 'search',
    event_screen: 'view_recipe_detail_screen',
    recipe_id: recipe.id,
  }

  const removeRecipeFromBasket = () => {
    removeRecipe(recipe.id)
    queueTrackingEvent<TrackCTAClick>({
      action: 'remove_recipe',
      properties: trackingProperties,
    })
  }

  const onClickCTA = (event: React.SyntheticEvent) => {
    event.stopPropagation()
    if (isInBasket) {
      removeRecipeFromBasket()
    } else {
      addRecipe(recipe.id)
      queueTrackingEvent<TrackCTAClick>({
        action: 'add_recipe',
        properties: trackingProperties,
      })
    }
  }

  const onKeyDown = (
    event: React.KeyboardEvent,
    callback: (event: React.KeyboardEvent) => void,
  ) => {
    if (event.code === 'Space' || event.code === 'Enter') {
      event.stopPropagation()
      event.preventDefault()
      callback(event)
    }
  }

  return (
    <Box
      aria-label="View recipe details"
      bg={Color.White}
      borderRadius={1.5}
      boxShadow="0 1px 6px rgba(0, 0, 0, 0.12)"
      color={Color.ColdGrey_900}
      display={Display.Flex}
      height={TILE_HEIGHT}
      onClick={onClick}
      onKeyDown={(event) => onKeyDown(event, onClick)}
      role="button"
      style={{ cursor: 'pointer', borderRadius: '6px', overflow: 'hidden' }}
      tabIndex={0}
    >
      <Box
        height="100%"
        minWidth={TILE_IMAGE_SIZE}
        overflowX={Overflow.Hidden}
        overflowY={Overflow.Hidden}
        width={TILE_IMAGE_SIZE}
      >
        {recipeImage ? (
          <Image
            alt={recipe.name}
            data-testid="search-recipe-tile"
            height="100%"
            src={recipeImage.url}
            style={{ display: 'block', objectFit: 'cover' }}
            width={TILE_IMAGE_SIZE}
          />
        ) : (
          <Box height="100%" width={TILE_IMAGE_SIZE} />
        )}
      </Box>
      <Box
        display={Display.Flex}
        flexDirection={FlexDirection.Column}
        flexGrow={1}
        flexShrink={1}
        minWidth="0"
      >
        <Box
          display={Display.Flex}
          flexGrow={1}
          flexShrink={1}
          justifyContent={JustifyContent.SpaceBetween}
          minWidth="0"
          paddingH={3}
          paddingV={2}
        >
          <Box
            alignItems={AlignItems.FlexStart}
            display={Display.Flex}
            flexGrow={1}
            gap={2}
            minWidth="0"
          >
            <Box flexGrow={1} flexShrink={1} minWidth="0">
              <Heading6
                style={{
                  display: '-webkit-box',
                  WebkitBoxOrient: 'vertical',
                  WebkitLineClamp: 2,
                  overflow: 'hidden',
                }}
              >
                {recipe.name}
              </Heading6>
              <Box display={Display.Flex}>
                {showCustomisedTileFooter ? (
                  <RecipeSubtitle
                    recipe={recipe}
                    menuPreferences={menuPreferences}
                    numPortions={recipeNumPortions}
                  />
                ) : (
                  <>
                    <Body2>{`${recipe.prepTime} mins`}</Body2>
                    {shouldShowCalories && (
                      <Body2>
                        &nbsp;&nbsp;|&nbsp;&nbsp;
                        {`${recipe.nutritionalInformation.perPortion.energyKcal}kcal`}
                      </Body2>
                    )}
                  </>
                )}
              </Box>
              {recipe.invalidPreference && (
                <Box paddingV={1}>
                  <InvalidPreferenceTag text={recipe.invalidPreference} />
                </Box>
              )}
            </Box>
            <Box
              display={Display.Flex}
              flexDirection={FlexDirection.Column}
              justifyContent={isInBasket ? JustifyContent.FlexStart : JustifyContent.FlexEnd}
              minWidth={ACTION_BUTTON_SIZE}
              style={{ alignSelf: 'stretch', flexShrink: 0 }}
            >
              {isInBasket ? (
                <Button
                  aria-label="Remove recipe"
                  colorVariant={ButtonColorVariant.Tertiary}
                  onClick={onClickCTA}
                  onKeyDown={(event) => onKeyDown(event, onClickCTA)}
                  width={ACTION_BUTTON_SIZE}
                  style={{
                    alignSelf: 'flex-end',
                    height: ACTION_BUTTON_SIZE,
                    minHeight: ACTION_BUTTON_SIZE,
                    minWidth: ACTION_BUTTON_SIZE,
                    padding: 0,
                  }}
                >
                  <Icon name="delete" variant={IconVariant.Actionable} size={5} />
                </Button>
              ) : (
                <Button
                  aria-label="Add recipe"
                  colorVariant={ButtonColorVariant.Primary}
                  disabled={reachedLimit}
                  icon="add"
                  onClick={onClickCTA}
                  onKeyDown={(event) => onKeyDown(event, onClickCTA)}
                  variant={ButtonVariant.Icon}
                  width={ACTION_BUTTON_SIZE}
                  style={{
                    alignSelf: 'flex-end',
                    height: ACTION_BUTTON_SIZE,
                    minHeight: ACTION_BUTTON_SIZE,
                    minWidth: ACTION_BUTTON_SIZE,
                    padding: 0,
                  }}
                />
              )}
            </Box>
          </Box>
        </Box>
        {isInBasket && (
          <Box
            alignItems={AlignItems.Center}
            display={Display.Flex}
            gap={2}
            height={TILE_FOOTER_HEIGHT}
            justifyContent={JustifyContent.FlexEnd}
            paddingH={3}
            paddingV={1}
            style={{ boxShadow: '0 -2px 8px rgba(0, 0, 0, 0.08)' }}
            onClick={(event: React.SyntheticEvent) => event.stopPropagation()}
            onKeyDown={(event: React.KeyboardEvent) => event.stopPropagation()}
          >
            <FlexPortionSummary
              recipeCount={recipeCount}
              portionCount={recipeNumPortions}
              onEdit={() => onEditFlexPortions?.(recipe, trackingData)}
              stopEventPropagation
            />
          </Box>
        )}
      </Box>
    </Box>
  )
}
